import React, { useEffect, useState, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import Box from 'components/elements/Box';
import Button from 'components/elements/button';
import formatDate from 'helpers/formatDate';
import {
  IExportJob,
  IEditJob,
  IAvailableCustomerResult,
  IOptionType,
  IHistoryData,
} from '../shared/shared';
import {
  ViewRow,
  JobTitle,
  JobStatus,
  DatePickerWrapper,
  DateLabel,
  Input,
  Variants,
  GoBackButton,
  FeedTypeSection,
} from '../shared/styles';
import { ReactComponent as SettingsIcon } from 'icons/icon-notification-settings.svg';
import { ReactComponent as Chevron } from 'icons/icon-chevron.svg';
import OpenMultiselectList from '../shared/MultiselectList';
import { ICountryFlagProps } from 'types/shared';
import ViewHistory from './ViewHistory';
import { subDays } from 'date-fns';
import sortBy from 'helpers/sortBy';
import Dropdown from 'components/base/Dropdown/Dropdown';

interface IViewJobProps {
  job?: IExportJob;
  jobCount: number;
  isAdmin?: boolean | '';
  customers: IAvailableCustomerResult[];
  countries: ICountryFlagProps[];
  saveJob: (job: IExportJob) => any;
  startJob: (jobId: string, date: Date) => void;
  backToJobList: () => void;
  shouldGetStatus: (shouldGet: boolean) => void;
  removeJob: (jobId: string) => void;
  jobHistory: IHistoryData[] | undefined;
  isSmallScreen: boolean;
  feedTypes?: any[];
}

const emptyJob: IEditJob = {
  customerIds: undefined,
  customerNames: '',
  description: '',
  destS3Bucket: '',
  frequency: 'daily',
  resultFormat: 'csv',
  resultFilePrefix: '',
  geoIds: [],
  lastRunStatus: 'completed',
  feedType: '',
};

const ViewJob: React.FC<IViewJobProps> = ({
  job,
  jobCount,
  isAdmin,
  customers,
  countries,
  saveJob,
  startJob,
  backToJobList,
  shouldGetStatus,
  // removeJob,
  jobHistory,
  isSmallScreen,
  feedTypes,
}) => {
  const [startDate, setStartDate] = useState<any>(new Date());
  const [editMode, setEditMode] = useState<boolean>(!job);

  const [editableJob, updateEditableJob] = useState<IEditJob>(job || emptyJob);

  const jobIsRunning =
    job?.lastRunStatus &&
    job?.lastRunStatus !== 'completed' &&
    job?.lastRunStatus !== 'failed';

  useEffect(() => {
    shouldGetStatus(!editMode);
  }, [editMode, shouldGetStatus]);

  const customerOptions: IOptionType[] = useMemo(() => {
    return customers
      .map((customer: IAvailableCustomerResult) => ({
        ...customer,
        feed: customer.feed.map((feedName: string) => {
          if (feedName === 'Streams') return 'streaming';
          return feedName.toLowerCase();
        }),
      }))
      .filter((customer: IAvailableCustomerResult) =>
        customer.feed.includes(editableJob.feedType)
      )
      .map((customer: IAvailableCustomerResult) => ({
        label: customer.displayName,
        value: customer.id.toString(),
      }))
      .sort(sortBy(['label']));
  }, [customers, editableJob.feedType]);

  const countryOptions: IOptionType[] = countries
    .map((country: ICountryFlagProps) => ({
      label: country.displayName,
      value: country.id.toString(),
    }))
    .sort(sortBy(['label']));

  const onJobStart = (job: IExportJob): void => {
    updateEditableJob({ ...editableJob, lastRunStatus: 'pending' });
    startJob(job.id, startDate);
  };

  const onJobSave = (event: any): void => {
    event.preventDefault();
    const data = new FormData(event.target);

    const jobForSaving: IEditJob = {
      ...editableJob,
      description:
        (data.get('description') as string) || editableJob.description,
      destS3Bucket:
        (data.get('destS3Bucket') as string) || editableJob.destS3Bucket,
      resultFilePrefix:
        (data.get('resultFilePrefix') as string) ||
        editableJob.resultFilePrefix,
    };

    const finalJob = { ...editableJob, ...jobForSaving };
    updateEditableJob(finalJob);
    saveJob(finalJob as IExportJob);
    setEditMode(false);
    if (!job) backToJobList();
  };

  const applyCustomerVariants = (selectedCustomers: string[]): void => {
    updateEditableJob({
      ...editableJob,
      customerIds: selectedCustomers,
      customerNames: selectedCustomers
        .map(
          (customerId: string) =>
            customers.find(
              (customer: IAvailableCustomerResult) =>
                customer.id === Number(customerId)
            )?.displayName
        )
        .join(', '),
    });
  };

  const applyCountryVariants = (selectedCountries: string[]): void => {
    updateEditableJob({
      ...editableJob,
      geoIds: selectedCountries,
      countries:
        selectedCountries && selectedCountries.length
          ? selectedCountries
              .map(
                (selectedCountry: string) =>
                  countries.find(
                    (country: ICountryFlagProps) =>
                      country.id === selectedCountry
                  )?.displayName
              )
              .join(', ')
          : 'Global',
    });
  };

  const availableFeedTypes = useMemo(() => {
    return feedTypes
      ? feedTypes.map((fType: any) => ({
          label: fType.id,
          value: fType.id,
          withoutSubLabel: true,
        }))
      : [];
  }, [feedTypes]);

  const onFeedTypeChange = (selectedType: any): void => {
    updateEditableJob({
      ...editableJob,
      feedType: selectedType.value,
      customerIds: undefined,
      customerNames: '',
    });
  };

  return (
    <>
      {!editMode && jobCount > 1 && (
        <GoBackButton
          type="button"
          onClick={(evt: MouseEvent): void => {
            evt.stopPropagation();
            backToJobList();
          }}
        >
          <Chevron aria-hidden /> Back to Job List
        </GoBackButton>
      )}
      <form onSubmit={onJobSave}>
        <Box my={2} padding="0 2.5rem">
          {!editMode && job ? (
            <Box display="flex">
              <JobTitle>{job.description}</JobTitle>
              {isAdmin && (
                <Button
                  onClick={(): void => setEditMode(true)}
                  display="flex"
                  marginLeft="auto"
                  marginRight="-2.2em"
                  paddingTop="0.7rem"
                >
                  <SettingsIcon />
                </Button>
              )}
            </Box>
          ) : (
            <ViewRow editMode={editMode}>
              <span>Description:</span>
              <Input
                type="text"
                name="description"
                defaultValue={editableJob.description}
              />
            </ViewRow>
          )}
          <ViewRow editMode={editMode}>
            <span>Feed Type:</span>
            {!editMode && job ? (
              job.feedType
            ) : !job && editMode ? (
              <Box width="70%">
                {feedTypes && (
                  <FeedTypeSection>
                    <Dropdown
                      items={availableFeedTypes}
                      selectedItem={{
                        label: editableJob?.feedType,
                        value: editableJob?.feedType,
                      }}
                      onChange={onFeedTypeChange}
                    />
                  </FeedTypeSection>
                )}
              </Box>
            ) : (
              <Box width="70%">{job?.feedType}</Box>
            )}
          </ViewRow>
          <ViewRow editMode={editMode}>
            <span>Customers:</span>
            {!editMode && job ? (
              job.customerNames
            ) : (
              <Box width="70%">
                <Variants>{editableJob.customerNames}</Variants>
                {customerOptions.length ? (
                  <OpenMultiselectList
                    title="Select Customers"
                    variants={customerOptions}
                    applyVariants={applyCustomerVariants}
                    selectedVariants={editableJob.customerIds}
                    isNarrow
                  />
                ) : (
                  <Box color="red" fontSize="12px">
                    There are no customers for selected Feed Type
                  </Box>
                )}
              </Box>
            )}
          </ViewRow>
          <ViewRow editMode={editMode}>
            <span>Countries:</span>
            {!editMode && job ? (
              job.countries
            ) : (
              <Box width="70%">
                <Variants>{editableJob.countries || 'Global'}</Variants>
                <OpenMultiselectList
                  title="Select Countries"
                  variants={countryOptions}
                  applyVariants={applyCountryVariants}
                  selectedVariants={editableJob.geoIds}
                />
              </Box>
            )}
          </ViewRow>
          {!editMode && job && (
            <ViewRow>
              <span>Frequency:</span>
              {job.frequency}
            </ViewRow>
          )}

          {!editMode && job && (
            <ViewRow>
              <span>S3 Bucket:</span>
              {job.destS3Bucket}
            </ViewRow>
          )}

          {editMode && !job && (
            <ViewRow editMode={editMode}>
              <span>S3 Bucket:</span>
              <Input
                type="text"
                name="destS3Bucket"
                defaultValue={editableJob.destS3Bucket}
              />
            </ViewRow>
          )}

          {!editMode && job && (
            <ViewRow>
              <span>Path:</span>
              {job.resultFilePrefix}
            </ViewRow>
          )}

          {/* <ViewRow editMode={editMode}>
            <span>Path:</span>
            {!editMode && job ? (
              job.resultFilePrefix
            ) : (
              <Input
                type="text"
                name="resultFilePrefix"
                defaultValue={editableJob.resultFilePrefix}
              />
            )}
          </ViewRow> */}

          {editMode && !job && (
            <ViewRow editMode={editMode}>
              <span>Path:</span>
              <Input
                type="text"
                name="resultFilePrefix"
                defaultValue={editableJob.resultFilePrefix}
              />
            </ViewRow>
          )}

          {!editMode && job && (
            <>
              <ViewRow>
                <span>Created:</span>
                {formatDate(job.createdAt, 'day-short')}
              </ViewRow>
              <ViewRow>
                <span>Last Updated:</span>
                {formatDate(job.lastUpdatedAt, 'day-short')}
              </ViewRow>
              {job.lastRunStatus && (
                <ViewRow>
                  <span>
                    {!jobIsRunning ? 'Last ' : ''}
                    Status:
                  </span>
                  <JobStatus status={job.lastRunStatus}>
                    {job.lastRunStatus}
                  </JobStatus>
                  {!isSmallScreen && !jobIsRunning && (
                    <ViewHistory
                      shouldGetStatus={shouldGetStatus}
                      jobName={job.description}
                      historyList={jobHistory?.filter(
                        (he) => he.jobId === job.id
                      )}
                      customers={customers}
                    />
                  )}
                </ViewRow>
              )}
            </>
          )}

          {editMode ? (
            <ViewRow display="flex" justifyContent="center" pt={4}>
              {job && (
                <>
                  <Button
                    onClick={(): void => {
                      setEditMode(false);
                      updateEditableJob(job);
                    }}
                    variant="secondary"
                    alignSelf="flex-end"
                    fontWeight="600"
                    borderRadius="3px"
                    height="28px"
                    px={4}
                    py={1}
                    mx={2}
                    type="button"
                  >
                    Cancel
                  </Button>
                </>
              )}
              <Button
                variant="primary"
                alignSelf="flex-end"
                fontWeight="600"
                borderRadius="3px"
                height="28px"
                px={4}
                py={1}
                mx={2}
                type="submit"
              >
                Save Job
              </Button>
              {/* {job && (
                <Button
                  onClick={(): void => {
                    if (
                      window.confirm(
                        'Are you sure you wish to delete this job?'
                      )
                    ) {
                      removeJob(job.id);
                      backToJobList();
                    }
                  }}
                  variant="destructive"
                  alignSelf="flex-end"
                  fontWeight="600"
                  borderRadius="3px"
                  height="28px"
                  px={4}
                  py={1}
                  mx={2}
                  type="button"
                >
                  Remove Job
                </Button>
              )} */}
            </ViewRow>
          ) : (
            // Run Job Section
            <ViewRow display="flex" justifyContent="flex-start" mt={4}>
              <DatePickerWrapper>
                <DateLabel>Select specific date:</DateLabel>
                <DatePicker
                  disabled={jobIsRunning}
                  selected={startDate}
                  onChange={setStartDate}
                  minDate={subDays(new Date(), 30)}
                  maxDate={new Date()}
                />
              </DatePickerWrapper>

              <Button
                onClick={(): void => {
                  if (job) onJobStart(job);
                }}
                variant={jobIsRunning ? 'disabled' : 'primary'}
                alignSelf="flex-end"
                fontWeight="600"
                borderRadius="3px"
                height="28px"
                py={1}
                px={4}
                type="button"
                disabled={jobIsRunning}
              >
                {jobIsRunning
                  ? isSmallScreen
                    ? 'Please wait'
                    : 'Please wait, job is running'
                  : 'Execute Job'}
              </Button>
            </ViewRow>
          )}
        </Box>
      </form>
    </>
  );
};

export default ViewJob;
